import { useApp, useBehavior, useList, useTriggerAction } from '@libs/hooks';
import { AssetsSvgMap, TItem, TWidgetProps } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import React from 'react';
import { TConsumerInputPhoneData, TConsumerItem, TConsumerListData } from '../../../types/data';
import { RowEnum } from '../../../types/enums';
import { TListOption } from '../../../types/options';
import { convertItemToRowComponentProps } from '../../../utils/helpers';
import { getInlineStyles } from '../../../utils/styles';
import RowComponent from '../../UI/Row/RowComponent';
import { Separator } from '../../Separator/Separator';
import { Modal } from '../../UI/Modal/Modal';
import InputSearch from '../InputSearch';
import ListItems, { TListItemProps } from '../ListItems';
import styles from '../styles/styles.module.css';
import { SEARCH_ENABLED_ITEMS_COUNT_DEFAULT } from '../utils/ListConstant';
import ListSearch from './ListSearch';
import ListSectionItems from './ListSectionItems';

type TListSelect = {
    isOpenInModal?: boolean;
    rowId?: RowEnum.SELECTOR_REGULAR | RowEnum.ROW_REGULAR;
    onSelectItem?: (uid: string, value: TItem) => void;
} & TWidgetProps<TConsumerListData & TConsumerInputPhoneData, TListOption>;

const convertItemsToListSelect = (items?: Array<TConsumerItem>, currentValue?: string, isDisabled?: boolean) =>
    Array.isArray(items) ? items?.map?.((item) => convertItemToListSelect(item, currentValue, isDisabled) as TConsumerItem) : [];
const convertItemToListSelect = (item: TConsumerItem, value?: string, isDisabled?: boolean): TConsumerItem => {
    return {
        ...item,
        isDisabled: isDisabled ? true : item.isDisabled,
        selected: item?.selected
            ? true
            : item.id?.toLowerCase?.() === value?.toLowerCase?.() || (item.value as string)?.toLowerCase?.() === value?.toLowerCase?.(),
    };
};

export const ListSelect: React.FC<TListSelect> = ({ widget, isOpenInModal = false, rowId, onSelectItem }) => {
    const searchEnabledItemsCount = widget.options?.searchEnabledItemsCount ?? SEARCH_ENABLED_ITEMS_COUNT_DEFAULT;
    const searchWidget = { ...widget, options: {} };
    const { isHidden, isDisabled } = useBehavior(widget);
    const { setModal, setPopup, setBottomSheet } = useApp();
    const { triggerActionsByType } = useTriggerAction(widget);
    const { handleClick } = useList(widget, { triggerActionsByType, setModal, setPopup, setBottomSheet });

    const dataForListSelect = {
        ...widget?.data,
        items: convertItemsToListSelect(widget?.data?.items, widget.data.value as string, isDisabled),
    };

    const closeModal = () => {
        if (isOpenInModal) {
            setModal(
                <Modal isCanCloseModal={true} iconName={AssetsSvgMap.ic_arrow_back} onHandleClick={() => setModal(null)}>
                    <ListSelect widget={widget} isOpenInModal={isOpenInModal} rowId={rowId} />
                </Modal>,
            );
        } else {
            setModal(null);
        }
    };

    const renderItem = ({ item, firstLetter, addLetter }: TListItemProps) => {
        const newItem = convertItemToRowComponentProps(item, rowId);
        if (newItem) {
            return (
                <React.Fragment key={item.id}>
                    <ConditionalRender condition={addLetter}>
                        <div className={styles.firstLetterRow}>
                            <div className={styles.firstLetter}>{firstLetter}</div>
                        </div>
                    </ConditionalRender>
                    <RowComponent
                        {...newItem}
                        handleClick={() => {
                            if (!newItem.isDisabled) {
                                if (onSelectItem) {
                                    onSelectItem(widget.uid, item);
                                } else {
                                    handleClick(widget.uid, item);
                                }
                            }
                        }}
                        id={rowId ?? RowEnum.ROW_REGULAR}
                    />
                </React.Fragment>
            );
        }
        return false;
    };

    const onInputSearchHandleClick = () => {
        setModal?.(
            <Modal isCanCloseModal={false} noTopPadding iconName={AssetsSvgMap.ic_arrow_back}>
                <ListSearch widget={widget} renderItem={renderItem} closeModal={closeModal} />
            </Modal>,
        );
    };

    if (!isHidden && Array.isArray(dataForListSelect.items) && dataForListSelect.items.length > 0) {
        const allItemsHeader = dataForListSelect?.allItemsHeader ?? dataForListSelect?.messages?.allItemsHeader;
        return (
            <div data-test={widget.uid} style={getInlineStyles({ margin: widget.options?.margin })}>
                <ListSectionItems data={dataForListSelect} renderItem={renderItem} />
                <ConditionalRender condition={!!allItemsHeader}>
                    <Separator isWidget={false} separatorVariant={'subheader'} text={allItemsHeader?.toLowerCase()} />
                </ConditionalRender>
                <ConditionalRender condition={dataForListSelect.items.length >= searchEnabledItemsCount}>
                    <InputSearch
                        widget={searchWidget}
                        asButton={true}
                        handleClick={onInputSearchHandleClick}
                        handleCloseClick={closeModal}
                        marginBottom={24}
                        marginLeft={24}
                        marginRight={24}
                    />
                </ConditionalRender>
                <ListItems items={dataForListSelect.items} renderItem={renderItem} sortByAlphabet={widget.options?.sortByAlphabet} />
            </div>
        );
    }
    return null;
};
